import React, { useState } from 'react';
import { ActivityIndicator, Linking, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { billingAPI } from '../../services/api';

export default function BillingRecoveryScreen() {
  const { billingStatus, refreshAuth, logout } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const paymentIssue = ['past_due', 'unpaid', 'incomplete', 'incomplete_expired', 'paused']
    .includes((billingStatus?.subscription_status || '').toLowerCase());

  const openBillingPortal = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    let popup: Window | null = null;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      popup = window.open('about:blank', '_blank');
    }
    try {
      const { url } = await billingAPI.portal();
      if (popup) popup.location.href = url;
      else await Linking.openURL(url);
    } catch {
      popup?.close();
      setError(t('billingRecovery.portalError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        <View style={[styles.icon, paymentIssue ? styles.warningIcon : styles.infoIcon]}>
          <MaterialIcons name={paymentIssue ? 'credit-card-off' : 'cloud-off'} size={34} color={paymentIssue ? '#B45309' : '#1D4ED8'} />
        </View>
        <Text style={styles.title}>
          {paymentIssue ? t('billingRecovery.paymentTitle') : t('billingRecovery.unavailableTitle')}
        </Text>
        <Text style={styles.message}>
          {paymentIssue ? t('billingRecovery.paymentMessage') : t('billingRecovery.unavailableMessage')}
        </Text>
        {error ? <Text style={styles.error}>{error}</Text> : null}
        {paymentIssue ? (
          <TouchableOpacity style={styles.primaryButton} onPress={openBillingPortal} disabled={loading} accessibilityRole="button">
            {loading ? <ActivityIndicator color="#fff" /> : <MaterialIcons name="open-in-new" size={19} color="#fff" />}
            <Text style={styles.primaryText}>{t('billingRecovery.updatePayment')}</Text>
          </TouchableOpacity>
        ) : null}
        <TouchableOpacity
          style={paymentIssue ? styles.secondaryButton : styles.primaryButton}
          onPress={() => refreshAuth({ forceBillingRefresh: true })}
          disabled={loading}
          accessibilityRole="button"
        >
          <MaterialIcons name="refresh" size={19} color={paymentIssue ? '#1D4ED8' : '#fff'} />
          <Text style={paymentIssue ? styles.secondaryText : styles.primaryText}>{t('billingRecovery.retry')}</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={logout} accessibilityRole="button" style={styles.signOutButton}>
          <Text style={styles.signOutText}>{t('billingRecovery.signOut')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC', alignItems: 'center', justifyContent: 'center', padding: 24 },
  card: { width: '100%', maxWidth: 440, backgroundColor: '#fff', borderRadius: 20, borderWidth: 1, borderColor: '#E2E8F0', padding: 28, alignItems: 'center' },
  icon: { width: 68, height: 68, borderRadius: 34, alignItems: 'center', justifyContent: 'center', marginBottom: 20 },
  warningIcon: { backgroundColor: '#FEF3C7' },
  infoIcon: { backgroundColor: '#DBEAFE' },
  title: { fontSize: 23, lineHeight: 30, fontWeight: '800', color: '#0F172A', textAlign: 'center' },
  message: { marginTop: 10, fontSize: 15, lineHeight: 22, color: '#475569', textAlign: 'center' },
  error: { marginTop: 14, color: '#B91C1C', fontSize: 14, textAlign: 'center' },
  primaryButton: { width: '100%', minHeight: 48, marginTop: 22, borderRadius: 10, backgroundColor: '#1D4ED8', flexDirection: 'row', gap: 8, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 18 },
  primaryText: { color: '#fff', fontSize: 15, fontWeight: '700' },
  secondaryButton: { width: '100%', minHeight: 48, marginTop: 12, borderRadius: 10, borderWidth: 1, borderColor: '#BFDBFE', backgroundColor: '#EFF6FF', flexDirection: 'row', gap: 8, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 18 },
  secondaryText: { color: '#1D4ED8', fontSize: 15, fontWeight: '700' },
  signOutButton: { marginTop: 18, padding: 8 },
  signOutText: { color: '#64748B', fontSize: 14, fontWeight: '600' },
});
